// src/helpers/handleUserMessage.js
import { sendFAQRequest } from './sendFAQRequest'; // Adjust the import path accordingly
import { sendRAGRequest } from './sendRAGRequest'; // Adjust the import path accordingly
import { handleGreetings } from './handleGreetings'; // Adjust the import path accordingly

/**
 * Handles the latest user message by processing greetings, checking FAQs,
 * and sending RAG requests if necessary.
 *
 * @param {Object} params - The parameters required for handling the message.
 * @param {Array} params.messages - The current array of message objects.
 * @param {Function} params.setMessages - The state setter function for messages.
 * @param {Function} params.t - The translation function from react-i18next.
 * @param {string} params.language - The current language from i18n.
 */

async function processUserQuestion(user_question) {
    // Cloud Function URL
    // const url = "https://us-central1-chatbot-418311.cloudfunctions.net/processText";
    const url = "https://europe-west3-chatbot-418311.cloudfunctions.net/process-text-europe";
    // Make a POST request to the Cloud Function
    const response = await fetch(url, {
        method: "POST",
        headers: {
            "Content-Type": "application/json"
        },
        body: JSON.stringify({ text: user_question })
    });

    try {
        // Parse the JSON response
        if (response.ok) {
            const data = await response.json();
            const redactedText = data.redactedText;
            const wasModified = data.wasModified;

            // Use the redacted text in your application
            return [redactedText, wasModified];
        } else {
            console.error("Error:", await response.text());
            throw new Error(`HTTP error! status: ${response.status}`);
        }
    } catch (error) {
        console.error("An error occurred while processing the user question:", error);
    }
}

export const handleUserMessage = async ({ messages, setMessages, t, language, isSimplifiedMode, onWasModified }) => {
    if (messages.length === 0) return;

    const lastMessage = messages[messages.length - 1];

    if (lastMessage.type !== 'user') return;

    // Handle greetings and get assistant response
    const assistantResponse = handleGreetings(lastMessage, t);


    let user_question = lastMessage.text

    // Check if the user_question matches 'chat.suggestions.useChat' translation
    if (user_question === t('chat.suggestions.useChat')) {
        var ans = t('chat.answers.useChat');
        return setMessages((prevMessages) => [
            ...prevMessages,
            { id: Date.now().toString(), type: 'assistant', text: ans },
        ]);
    }

    // Check if the user_question matches 'chat.suggestions.contactMunicipality' translation
    if (user_question === t('chat.suggestions.contactMunicipality')) {
        var ans = t('chat.answers.contactMunicipality');
        return setMessages((prevMessages) => [
            ...prevMessages,
            { id: Date.now().toString(), type: 'assistant', text: ans },
        ]);
    }

    // assistant_thinking
    setMessages((prevMessages) => [...prevMessages, assistantResponse]);


    if (assistantResponse.text === t('chat.thinkingPlaceholder')) {
        try {
            const FAQ = await sendFAQRequest(lastMessage.text, language);
            if (FAQ && FAQ.category && FAQ.category !== 'ELSE') { // Check if category is not 'ELSE'

                const translationKey = `chat.FAQ.${FAQ.category}`;
                if (t(translationKey)) {
                    const faqAnswer = t(translationKey);
                    const updatedAssistantMessage = {
                        id: Date.now().toString(),
                        type: 'assistant',
                        text: faqAnswer,
                    };

                    setMessages((prevMessages) => {
                        const updatedMessages = [...prevMessages];
                        const last = updatedMessages[updatedMessages.length - 1];
                        if (last.type === 'assistant_thinking') {
                            console.log("ASSISTANT THINKING")
                            updatedMessages[updatedMessages.length - 1] = updatedAssistantMessage;
                        } else {
                            console.log(" NO ASSISTANT THINKING", last.type)
                            updatedMessages.push(updatedAssistantMessage);
                        }
                        return updatedMessages;
                    });
                    return; // Exit after handling FAQ
                }
            }

            const memory =
                messages
                    .filter((msg) => msg.type === 'user' && msg.id !== lastMessage.id)
                    .map((msg) => msg.text)
                    .join(' ') || 'Hello';



            // Add info reduction:
            const [redactedText, wasModified] = await processUserQuestion(user_question);

            if (redactedText) {
                user_question = redactedText;

                if (onWasModified) {
                    onWasModified(wasModified);
                }
            }



            const ragResponse = await sendRAGRequest(user_question, memory, language, isSimplifiedMode);
            if (ragResponse && ragResponse.answer) {
                let ans = ragResponse.answer;
                let src = ragResponse.sources;
                let srcRqs = ragResponse.sourcesRequested;

                // Override response if it contains specific phrases
                if (
                    ans.includes('are trained on data up') ||
                    ans.includes('bent getraind op gegevens') ||
                    ans.includes('تم تدريبهم على البيانات حتى') || // Arabic
                    ans.includes('zijn getraind op gegevens tot')   // Dutch
                ) {
                    ans = t('outOfScopeError');
                }

                const ragAssistantMessage = {
                    id: Date.now().toString(),
                    type: 'assistant',
                    text: ans,
                };

                setMessages((prevMessages) => {
                    const updatedMessages = [...prevMessages];
                    const last = updatedMessages[updatedMessages.length - 1];
                    if (last.type === 'assistant_thinking' && last.text === t('chat.thinkingPlaceholder')) {
                        updatedMessages[updatedMessages.length - 1] = ragAssistantMessage;
                    } else {
                        updatedMessages.push(ragAssistantMessage);
                    }
                    return updatedMessages;
                });

                // If the sources were requested (ie user typed r=1 or r=2 at the end of the message) they will be displayed:
                if (srcRqs === true) {
                    // Format the sources into a readable string
                    const formattedSources = src.map((source, index) => {
                        return `**Document ${index + 1}:** ${source.metadata.document}\n**Content:** ${source.content}`;
                    }).join('\n\n');

                    const ragAssistantMessage2 = {
                        id: Date.now().toString(),
                        type: 'assistant',
                        text: formattedSources,
                    };
                    setMessages((prevMessages) => {
                        const updatedMessages = [...prevMessages];
                        updatedMessages.push(ragAssistantMessage2);
                        return updatedMessages;
                    });
                }
            } else {
                // Generic error response
                const assistantMessage = {
                    id: Date.now().toString(),
                    type: 'assistant',
                    text: t('chat.genericError'),
                };
                setMessages((prevMessages) => {
                    const updatedMessages = [...prevMessages];
                    const last = updatedMessages[updatedMessages.length - 1];
                    if (last.type === 'assistant_thinking' && last.text === t('chat.thinkingPlaceholder')) {
                        updatedMessages[updatedMessages.length - 1] = assistantMessage;
                    } else {
                        updatedMessages.push(assistantMessage);
                    }
                    return updatedMessages;
                });
            }
        } catch (error) {
            console.error('Error processing FAQ or RAG request:', error);
            const assistantMessage = {
                id: Date.now().toString(),
                type: 'assistant',
                text: t('chat.genericError'),
            };
            setMessages((prevMessages) => {
                const updatedMessages = [...prevMessages];
                const last = updatedMessages[updatedMessages.length - 1];
                if (last.type === 'assistant_thinking' && last.text === t('chat.thinkingPlaceholder')) {
                    updatedMessages[updatedMessages.length - 1] = assistantMessage;
                } else {
                    updatedMessages.push(assistantMessage);
                }
                return updatedMessages;
            });
        }
    }
};
